/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import apiService from "services/apiService";
import { UploadCloud, Settings, BookOpen, BarChart2, FileText } from "lucide-react";
import { useToaster } from "contexts/toasterContext";
import { Training } from "../sharedUtils";

// Import Views and Modals
import { TrainingCard, EmployeeProgress } from "./AdminTrainingViews";
import { UploadTrainingModal, AssignTrainingModal } from "./AdminTrainingModals";
import ManageDomainsModal from "./ManageDomainsModal"; // CHANGED: Now imports from its own file
import GenericModal from "components/Modal";

export default function AdminTrainingPage() {
    const { addToast } = useToaster();
    const navigate = useNavigate();

    // State
    const [activeTab, setActiveTab] = useState<"TRAININGS" | "EMPLOYEE_PROGRESS">("TRAININGS");
    const [trainings, setTrainings] = useState<Training[]>([]);
    const [loading, setLoading] = useState(false);
    const [generatingId, setGeneratingId] = useState<number | null>(null);
    const [openDeleteModal, setOpenDeleteModal] = useState(false);
    const [selectedTrainingId, setSelectedTrainingId] = useState<number | null>(null);
    const [deleting, setDeleting] = useState(false);

    // Modal Visibility
    const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
    const [isManageDomainsModalOpen, setIsManageDomainsModalOpen] = useState(false);
    const [assignTrainingId, setAssignTrainingId] = useState<number | null>(null);

    // Fetch Logic & Polling
    const fetchTrainings = async (showLoader = false) => {
        if (showLoader) setLoading(true);
        try {
            const res = await apiService.get("api/gp/trainings");
            setTrainings(res.data || []);
        } catch (error: any) {
            if (showLoader) addToast(error?.response?.data?.error || "Failed to load trainings.", "error");
        } finally {
            if (showLoader) setLoading(false);
        }
    };

    useEffect(() => { fetchTrainings(true); }, []);

    useEffect(() => {
        const needsPolling = trainings.some(t => t.trainingStatus === "PROCESSING" || t.trainingStatus === "GENERATING");
        let intervalId: NodeJS.Timeout;
        if (needsPolling) intervalId = setInterval(() => fetchTrainings(false), 5000);
        return () => clearInterval(intervalId);
    }, [trainings]);

    // Handlers
    const handleGenerateExercise = async (trainingId: number) => {
        try {
            setGeneratingId(trainingId);
            setTrainings(prev => prev.map(t => t.trainingId === trainingId ? { ...t, trainingStatus: "GENERATING" } : t));
            const res = await apiService.post(`api/gp/exercises/generate/${trainingId}`, {});
            addToast(res.data?.message || "Exercise generation started.", "success");
        } catch (error: any) {
            addToast(error?.response?.data?.error || "Failed to start exercise generation.", "error");
            fetchTrainings();
        } finally {
            setGeneratingId(null);
        }
    };

    const handleDeleteTraining = (id: number) => {
        setSelectedTrainingId(id);
        setOpenDeleteModal(true);
    };
    const handleConfirmDelete = async () => {
        if (!selectedTrainingId || deleting) return;

        setDeleting(true);

        try {
            await apiService.delete(`api/gp/trainings/${selectedTrainingId}`);

            addToast("Training deleted successfully.", "success");

            setOpenDeleteModal(false);
            setSelectedTrainingId(null);

            fetchTrainings();
        } catch (e: any) {
            addToast(
                e?.response?.data?.error || "Failed to delete training.",
                "error"
            );
        } finally {
            setDeleting(false);
        }
    };

    const handlePreview = (id: number) => {
        navigate(`/admin/training/${id}/preview`);
    };

    return (
        <div className="flex flex-col h-full min-h-[calc(100vh-100px)] bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-800 overflow-hidden shadow-sm transition-colors duration-300">

            {/* TABS HEADER */}
            <div className="px-6 pt-2 border-b border-gray-200 dark:border-gray-800/80 shrink-0 bg-gray-50/50 dark:bg-gray-800/50">
                <div className="flex justify-between items-center mb-2">
                    <div className="flex items-center gap-3">
                        <BookOpen className="text-blue-600 dark:text-blue-400" size={24} />
                        <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Training Management</h2>
                    </div>
                </div>
                <div className="flex gap-6 border-b-2 border-transparent">
                    <button onClick={() => setActiveTab("TRAININGS")} className={`pb-2 text-sm font-medium transition-colors relative flex items-center gap-2 ${activeTab === "TRAININGS" ? "text-blue-600 dark:text-blue-400" : "text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"}`}>
                        <BookOpen size={16} /> Trainings
                        {activeTab === "TRAININGS" && <span className="absolute bottom-[-2px] left-0 w-full h-0.5 bg-blue-600 dark:bg-blue-400 rounded-t-full" />}
                    </button>
                    <button onClick={() => setActiveTab("EMPLOYEE_PROGRESS")} className={`pb-2 text-sm font-medium transition-colors relative flex items-center gap-2 ${activeTab === "EMPLOYEE_PROGRESS" ? "text-blue-600 dark:text-blue-400" : "text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"}`}>
                        <BarChart2 size={16} /> Employee Progress
                        {activeTab === "EMPLOYEE_PROGRESS" && <span className="absolute bottom-[-2px] left-0 w-full h-0.5 bg-blue-600 dark:bg-blue-400 rounded-t-full" />}
                    </button>
                </div>
            </div>

            {/* CONTENT AREA */}
            <div className="flex-1 overflow-y-auto p-2">
                {activeTab === "TRAININGS" && (
                    <>
                        <div className="flex flex-wrap gap-3 mb-2">
                            <button onClick={() => setIsUploadModalOpen(true)} className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors shadow-sm">
                                <UploadCloud size={16} /> Upload Training
                            </button>
                            <button onClick={() => setIsManageDomainsModalOpen(true)} className="flex items-center gap-2 px-4 py-2 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 border border-gray-300 dark:border-gray-600 rounded-lg text-sm font-medium hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors shadow-sm">
                                <Settings size={16} /> Manage Domains
                            </button>
                        </div>

                        {loading ? (
                            <div className="flex items-center justify-center py-12"><p className="text-gray-500">Loading trainings...</p></div>
                        ) : trainings.length === 0 ? (
                            <div className="flex flex-col items-center justify-center py-16 text-center">
                                <FileText size={48} className="text-gray-300 dark:text-gray-600 mb-4" />
                                <h3 className="text-lg font-medium text-gray-900 dark:text-white">No Trainings Found</h3>
                                <p className="text-sm text-gray-500 mt-1">Upload a document to generate your first training module.</p>
                            </div>
                        ) : (
                                    <div className="grid gap-2 grid-cols-1 md:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4 pb-2">
                                {trainings.map((training) => (
                                    <TrainingCard
                                        key={training.trainingId}
                                        training={training}
                                        generatingId={generatingId}
                                        onGenerate={handleGenerateExercise}
                                        onAssign={(id) => setAssignTrainingId(id)}
                                        onPreview={handlePreview}
                                        onDelete={handleDeleteTraining}
                                    />
                                ))}
                            </div>
                        )}
                    </>
                )}

                {activeTab === "EMPLOYEE_PROGRESS" && <EmployeeProgress />}
            </div>

            <GenericModal
                isOpen={openDeleteModal}
                onClose={() => {
                    setOpenDeleteModal(false);
                    setSelectedTrainingId(null);
                }}
                onAccept={handleConfirmDelete}
                onDecline={() => {
                    setOpenDeleteModal(false);
                    setSelectedTrainingId(null);
                }}
                acceptText={deleting ? "Deleting..." : "Delete"}
                cancelText="Cancel"
                header="Delete Training"
                customWidth="500px"
            >
                <p className="text-base text-slate-600 dark:text-slate-300 mt-2">
                    Do you want to delete this training? This action cannot be undone.
                </p>
            </GenericModal>

            {/* MODALS */}
            <UploadTrainingModal isOpen={isUploadModalOpen} onClose={() => setIsUploadModalOpen(false)} onSuccess={() => fetchTrainings()} />
            <ManageDomainsModal isOpen={isManageDomainsModalOpen} onClose={() => setIsManageDomainsModalOpen(false)} />
            <AssignTrainingModal isOpen={assignTrainingId !== null} trainingId={assignTrainingId} onClose={() => setAssignTrainingId(null)} />
        </div>
    );
}